/* eslint-disable react/prop-types */
import React, { useMemo } from 'react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Chip, Paper, Typography } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import PhoneIcon from '@mui/icons-material/Phone';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import {
  GUEST_CHIP_COLOR,
  GUEST_CHIP_LABEL,
  STATUS_HEX,
  STATUS_LABELS,
  TEAL,
  TEAL_BORDER,
  TEAL_LIGHT,
  formatAppointmentNo,
  formatDisplayTime,
  getQueueSynopsis,
  groupAppointmentsByDoctor,
  resolveDoctorName,
  resolvePatientMobile,
  resolvePatientName,
} from '../constants';
import DoctorQueueSynopsis from './DoctorQueueSynopsis';
import QuickActionIcons from './QuickActionIcons';

// Same compact "Paper, left status border, chip + name + phone inline" look
// as layouts/followUps/index.js's FollowupRow, plus a drag handle + serial chip.
function SortableRow({ appointment, index, onSelect, onChanged, isInQueue, isNextQueue }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: appointment.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const statusColor = STATUS_HEX[appointment.status] || '#90a4ae';
  const patientName = resolvePatientName(appointment, appointment.irm_contacts_id);
  const mobile = resolvePatientMobile(appointment);

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      variant="outlined"
      sx={{
        mb: 0.75,
        borderLeft: `3px solid ${statusColor}`,
        borderRadius: 1.5,
        overflow: 'hidden',
        outline: isInQueue ? `2px solid ${statusColor}` : isNextQueue ? '2px dashed #90a4ae' : 'none',
        transition: 'box-shadow 0.15s, border-color 0.15s',
        '&:hover': { boxShadow: '0 1px 8px rgba(0,121,142,0.1)', borderColor: statusColor },
      }}
    >
      <Box sx={{ px: 1.25, py: 1.1, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box {...attributes} {...listeners} sx={{ cursor: 'grab', color: '#bbb', display: 'flex' }}>
          <DragIndicatorIcon fontSize="small" />
        </Box>
        <Chip label={index + 1} size="small" sx={{ minWidth: 28, height: 20, fontSize: '0.65rem', fontWeight: 700, borderRadius: 4 }} />

        <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => onSelect(appointment.id)}>
          <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
            <Chip
              label={STATUS_LABELS[appointment.status] || appointment.status}
              size="small"
              sx={{
                height: 17, fontSize: '0.6rem', fontWeight: 700,
                bgcolor: `${statusColor}18`, color: statusColor,
                border: `1px solid ${statusColor}44`,
                '& .MuiChip-label': { px: 0.7 },
              }}
            />
            <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700, color: statusColor, fontSize: '0.68rem' }}>
              {formatAppointmentNo(appointment)}
            </Typography>
            <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.83rem', color: '#222' }}>
              {patientName}
            </Typography>
            {appointment.is_guest === 'Y' && (
              <Chip label={GUEST_CHIP_LABEL} size="small" sx={{ height: 16, fontSize: '0.55rem', bgcolor: GUEST_CHIP_COLOR, color: '#fff', '& .MuiChip-label': { px: 0.7 } }} />
            )}
            {mobile && (
              <Box display="flex" alignItems="center" gap={0.25}>
                <PhoneIcon sx={{ fontSize: 10, color: '#ccc' }} />
                <Typography variant="caption" sx={{ color: '#999', fontSize: '0.68rem' }}>{mobile}</Typography>
              </Box>
            )}
          </Box>
          <Typography variant="caption" sx={{ display: 'block', color: '#888', fontSize: '0.68rem', mt: 0.35, pl: 0.25 }}>
            {formatDisplayTime(appointment.appointment_time) || 'No time set'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {isInQueue && <Chip label="IN" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: statusColor, color: '#fff !important', fontWeight: 700, borderRadius: 4 }} />}
          {isNextQueue && <Chip label="NEXT" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, borderRadius: 4 }} />}
          <QuickActionIcons appointment={appointment} onChanged={onChanged} />
        </Box>
      </Box>
    </Paper>
  );
}

// One doctor's queue: synopsis banner + drag-reorderable sortable list.
// Reused both for the single-doctor-selected mode and, once per doctor, for
// the "All Doctors" grouped mode.
function DoctorQueueBlock({ doctorId, doctorInfo, appointments, onSelect, onChanged, onReorder, showDoctorHeader }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const { inAppointment, nextAppointment } = useMemo(() => getQueueSynopsis(appointments), [appointments]);

  const handleDragEnd = event => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = appointments.findIndex(a => a.id === active.id);
    const newIndex = appointments.findIndex(a => a.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(appointments, oldIndex, newIndex);
    onReorder(doctorId, reordered.map(a => a.id));
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 1.5,
          py: 1,
          mb: 1.5,
          bgcolor: TEAL_LIGHT,
          borderRadius: 2,
          border: `1px solid ${TEAL_BORDER}`,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#2d3748', display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {showDoctorHeader && <LocalHospitalIcon sx={{ color: TEAL, fontSize: 18 }} />}
          {showDoctorHeader
            ? resolveDoctorName(doctorInfo, doctorId)
            : `${resolveDoctorName(doctorInfo, doctorId)} — drag to reorder the queue`}
        </Typography>
        <DoctorQueueSynopsis inAppointment={inAppointment} nextAppointment={nextAppointment} />
      </Box>

      {appointments.length === 0 ? (
        <Box textAlign="center" py={4}>
          <Typography variant="body2" color="textSecondary">
            No appointments for this doctor on the selected date.
          </Typography>
        </Box>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={appointments.map(a => a.id)} strategy={verticalListSortingStrategy}>
            {appointments.map((appointment, index) => (
              <SortableRow
                key={appointment.id}
                appointment={appointment}
                index={index}
                onSelect={onSelect}
                onChanged={onChanged}
                isInQueue={inAppointment?.id === appointment.id}
                isNextQueue={nextAppointment?.id === appointment.id}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}
    </Box>
  );
}

// appointments must already be pre-filtered to one date. If doctorId is set,
// they must also already be pre-filtered to that one doctor (single-queue
// mode, sorted by serial_no). If doctorId is empty ("All Doctors"),
// appointments span every doctor for that date and get grouped here.
// onReorder(doctorId, orderedIds:number[]) fires after a drag-reorder.
export default function DoctorSerialView({ appointments, doctorInfo, doctorId, onSelect, onChanged, onReorder }) {
  const doctorGroups = useMemo(() => groupAppointmentsByDoctor(appointments), [appointments]);

  if (!doctorId) {
    if (appointments.length === 0) {
      return (
        <Box textAlign="center" py={6}>
          <EventBusyIcon sx={{ fontSize: 44, color: '#e0e0e0', mb: 1 }} />
          <Typography variant="body2" color="textSecondary">
            No appointments for any doctor on the selected date.
          </Typography>
        </Box>
      );
    }
    return (
      <Box>
        {doctorGroups.map(group => (
          <DoctorQueueBlock
            key={group.doctorId}
            doctorId={group.doctorId}
            doctorInfo={group.doctorInfo}
            appointments={group.appointments}
            onSelect={onSelect}
            onChanged={onChanged}
            onReorder={onReorder}
            showDoctorHeader
          />
        ))}
      </Box>
    );
  }

  return (
    <DoctorQueueBlock
      doctorId={doctorId}
      doctorInfo={doctorInfo}
      appointments={appointments}
      onSelect={onSelect}
      onChanged={onChanged}
      onReorder={onReorder}
    />
  );
}
